<script lang="ts">
	//Animation
	import { fade } from 'svelte/transition';

	//Hooks
	import { onMount, beforeUpdate } from 'svelte';

	//Services
	import { format } from 'svelte-i18n';

	//Stores
	import {
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';

	//Helpers
	import { requestUserColors } from '../../../helpers/colors_helpers';

	//Design system components
	import { ColorWheel, CardGrid, FavColor, toast, Loader } from '@Shelly/shelly-design-system';

	//Helpers
	import {
		calculateRGB,
		cloudGroupRequestV2,
		getDevicesFormatted,
		handleCloudLocalLists,
		localGroupRequest,
	} from './control_group_helpers';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { colorLimited } from 'src/helpers/utilities';

	//Types
	import type { Device } from '../../../types/Device';

	//Components
	import ControlColorSelected from './ControlColorSelected.svelte';
	import { get_local_status } from 'src/services/local_requests';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { postReq } from 'src/services/http';

	export let color_mode_lights = [];

	$: memberStatusStore = createMemberStatusSnapshot(color_mode_lights.map((d) => d.id));
	$: memberStatus = $memberStatusStore;

	const firstDeviceRealId = real_id(color_mode_lights[0]?.id);

	let cloud = {} as Device,
		local = {} as Device,
		value = calculateRGB(memberStatus[firstDeviceRealId]),
		loading = false,
		favoriteColors = [],
		cacheValue = JSON.stringify(value).toString(),
		loadingFavorites = false;

	let favColors1Mapped;

	const updateCurrentColors = (_value) => {
		favColors1Mapped = favoriteColors.map((c) => {
			const currentColor = `rgb(${c.r}, ${c.g}, ${c.b})`;
			const isActive = `rgb(${_value.r}, ${_value.g}, ${_value.b})` === currentColor;

			return {
				active: isActive,
				color: currentColor,
				colorObj: c,
			};
		});
	};

	onMount(async () => {
		loadingFavorites = true;
		const favColorsReq = await requestUserColors();
		favoriteColors = favColorsReq ? favColorsReq : [];
		loadingFavorites = false;
	});

	beforeUpdate(() => {
		let lists = handleCloudLocalLists(
			Array.from(Object.values(color_mode_lights).map((d: Device) => d.id)),
		);
		[cloud, local] = [lists.cloud, lists.local];

		/**
		 * Don't pull color from status while a user-initiated change is in flight,
		 * otherwise stale status (typical for cloud groups, where we don't refetch
		 * after a successful set) clobbers the optimistic `value` and snaps the
		 * color wheel selector back to the previous color (BUG: click on wheel
		 * didn't move the indicator, fav bar showed wrong active color).
		 */
		if (!loading) {
			const statusValue = calculateRGB(memberStatus[firstDeviceRealId]);
			if (cacheValue !== JSON.stringify(statusValue).toString()) {
				value = statusValue;
				cacheValue = JSON.stringify(value).toString();
			}
		}

		favColors1Mapped = favoriteColors.map((c) => {
			const currentColor = `rgb(${c.r}, ${c.g}, ${c.b})`;
			const isActive = `rgb(${value.r}, ${value.g}, ${value.b})` === currentColor;

			return {
				active: isActive,
				color: currentColor,
				colorObj: c,
			};
		});
	});

	/**
	 * Persists the current favorite-colors list to the user account.
	 *
	 * Favorite colors are USER-scoped (saved on the account), not per-device. The
	 * previous group-only branch pushed them through `/v2/devices/set/groups` as a
	 * per-device command, which devices like the Plus RGBW PM correctly reject
	 * with `failedCommands: { [id]: 'DEVICE_UNSUPPORTED_COMMAND' }`. That triggered
	 * the "Something went wrong!" toast even though the API responded 200 OK and
	 * the favorite was saved on the account. Mirrors the single-device path in
	 * `ControlColor.svelte` and just calls the user-scoped endpoint.
	 */
	const updateColorsList = async () => {
		favoriteColors = favColors1Mapped.map((fc) => ({ ...fc.colorObj }));

		const res = await postReq('/interface/user_colors/save', {
			data: JSON.stringify({ colors: favoriteColors }),
		});

		if (!res?.isok) console.error('Failed to set colors');
	};

	/**
	 * Optimistically reflects the picked color in every group device's status
	 * (`devices_status` store), so `beforeUpdate`'s "sync value from status" guard
	 * doesn't immediately overwrite our new `value` with the stale pre-pick status
	 * (cloud groups don't refetch status after the set).
	 *
	 * Handles both Gen1 (`status.lights[channel]` with `{ red, green, blue }`) and
	 * Gen2++ (`status[component:channel].rgb` triplet).
	 *
	 * @param newColor - The RGB triplet to write into each device's status.
	 */
	const optimisticStatusUpdate = (newColor: { r: number; g: number; b: number }) => {
		const targets = [...Object.values(cloud), ...Object.values(local)] as Array<{
			id: string;
		}>;

		for (const target of targets) {
			const realId = real_id(target.id);
			const currentStatus = memberStatus[realId];
			const fullDevice = $devices[target.id] || $devices[realId];
			if (!currentStatus || !fullDevice) continue;

			let nextStatus;
			if (currentStatus.lights && Array.isArray(currentStatus.lights)) {
				const channel = fullDevice.channel ?? 0;
				nextStatus = {
					...currentStatus,
					lights: currentStatus.lights.map((light, i) =>
						i === channel
							? { ...light, red: newColor.r, green: newColor.g, blue: newColor.b }
							: light,
					),
				};
			} else {
				const compPath = get_gen_2_component_channel_structure(fullDevice);
				if (!currentStatus[compPath]) continue;
				nextStatus = {
					...currentStatus,
					[compPath]: {
						...currentStatus[compPath],
						rgb: [newColor.r, newColor.g, newColor.b],
					},
				};
			}

			devices_status.update_device_status({ id: realId, msg: nextStatus });
		}
	};

	/**
	 * Dispatches a Color/RGB change to every device in the group (cloud + local),
	 * with optimistic UI: `value`, the cache guard, the fav-bar mapping and the
	 * first device's status are updated immediately so the wheel selector lands
	 * exactly where the user clicked. On any failure (cloud `failedCommands` or a
	 * local request returning `!isok`), the optimistic state is rolled back to
	 * `previousValue` and an error toast is surfaced.
	 *
	 * Without the optimistic update, clicking on the wheel made the selector snap
	 * back to the previous color (drag worked because the wheel tracks the drag
	 * locally) and the fav bar kept the previous color highlighted as active, so
	 * the new color couldn't be saved as a favorite.
	 *
	 * @param newColor - The RGB triplet picked from the wheel or a fav swatch.
	 */
	const onSelectColor = async (newColor) => {
		const previousValue = { ...value };
		const data = {
			red: newColor.r,
			green: newColor.g,
			blue: newColor.b,
		};

		let anyFailure = false;
		loading = true;

		value = { r: data.red, g: data.green, b: data.blue };

		cacheValue = JSON.stringify(value).toString();
		updateCurrentColors(value);
		optimisticStatusUpdate(value);

		if (Object.keys(cloud).length > 0) {
			const tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			const res = await cloudGroupRequestV2(`/v2/devices/set/groups`, tmpDevices, data);
			if (typeof res === 'undefined' || Object.keys(res?.failedCommands ?? {}).length > 0) {
				anyFailure = true;
			}
		}

		if (Object.keys(local).length > 0) {
			for (const id in local) {
				let res;
				if (is_gen_2($devices[id])) {
					//supports plus rgbw for now
					const rgb =
						$devices[id].type === 'SNDC-0D4P10WW'
							? [data.red, data.green, data.blue]
							: [
									colorLimited(data.red, 'to'),
									colorLimited(data.green, 'to'),
									colorLimited(data.blue, 'to'),
								];
					res = await localGroupRequest(
						local[id],
						{ rgb },
						`${get_gen_2_component_from_category($devices[id])}.set`,
					);
				} else {
					res = await localGroupRequest(local[id], data, 'post');
				}
				if (res.isok) {
					const status_req = await get_local_status($devices[id], $devices[id].ip);
					let newStatus = {
						...memberStatus[real_id(id)],
						...status_req,
					};
					devices_status.update_device_status({
						id: real_id(id),
						msg: newStatus,
					});
					await update_settings($devices[id]);
				} else {
					anyFailure = true;
				}
			}
		}

		if (anyFailure) {
			value = previousValue;
			cacheValue = JSON.stringify(value).toString();
			updateCurrentColors(value);
			optimisticStatusUpdate(value);
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		loading = false;
	};

	// fav color abstraction will go here
	const onSaveColor = async (newColor) => {
		favColors1Mapped = [
			...favColors1Mapped,
			{
				active: true,
				color: `rgb(${newColor.r}, ${newColor.g}, ${newColor.b})`,
				colorObj: newColor,
			},
		];
		await updateColorsList();
	};

	const onDeleteColor = async (index: number) => {
		favColors1Mapped = favColors1Mapped.filter((_, i) => i !== index);
		await updateColorsList();
	};
</script>

<div in:fade|global={{ duration: 300 }}>
	<ColorWheel
		id="colorwheel-jqm0jnq"
		class="mb-xl"
		color={value}
		hideSaveBtn
		on:color_change={async (event) => {
			await onSelectColor(event.detail.color);
		}}
		disabled={loading}
		on:save_click={async (event) => await onSaveColor(event.detail.color)}
	/>
	{#if loadingFavorites}
		<Loader class="mb-m" />
	{:else}
		<ControlColorSelected
			class="mb-m"
			selectedColor={value}
			showSaveButton={!favColors1Mapped.some(
				(fc) => JSON.stringify(fc.colorObj) === JSON.stringify(value),
			)}
			on:save_color={(event) => onSaveColor(event.detail)}
		/>
		<CardGrid sm={90} class="mb-l">
			{#each favColors1Mapped as favColor, i}
				<FavColor
					a11yProps={{
						deleteButtonAriaLabel: $format('delete_color', { default: 'Delete color' }),
						favColorAriaLabel: $format('fav_color', { default: 'Favorite color' }),
					}}
					color={favColor.color}
					on:click={async () => await onSelectColor(favColor.colorObj)}
					on:delete={async () => await onDeleteColor(i)}
					active={favColor.active}
				/>
			{/each}
		</CardGrid>
	{/if}
</div>
